import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import {
  Drawer,
  DrawerProps,
  KeyValueList,
  NumberDataDisplay,
} from "@validereinc/common-components";
import {
  AssetTypeType,
  DefaultRecordValueConfigurationFunctionNames,
  DefaultRecordValueConfigurationType,
  DefaultRecordValueConfigurationTypeNames as DRVCTypeNames,
  DefaultRecordValueConfigurationType as DRVCTypesType,
} from "@validereinc/domain";
import React from "react";
import { useDRVCTargetResolve } from "./DefaultRecordValueConfigurationDrawer.helpers";

export const DefaultRecordValueConfigurationDrawer = ({
  isOpen,
  onClose,
  drvc,
}: {
  drvc?: DefaultRecordValueConfigurationType;
  assetType: AssetTypeType;
} & Pick<DrawerProps, "onClose" | "isOpen">) => {
  const { getTypeName, getUnitName } = useMeasurementTypes();

  const { isLoading, resolvedValues, dateRange, window } = useDRVCTargetResolve(
    {
      drvc,
    }
  );

  const data = [
    {
      title: "Function",
      value:
        DefaultRecordValueConfigurationFunctionNames[
          drvc?.configuration?.aggregate_function ?? "top"
        ],
    },
    {
      title: "Type",
      value: drvc?.configuration?.configuration_type
        ? DRVCTypeNames[drvc?.configuration?.configuration_type]
        : "",
    },
    ...resolvedValues.map((target: { name: string; url: string }) => ({
      title: "Target",
      value: target.url ? (
        <RoutingLink to={target.url}>{target.name}</RoutingLink>
      ) : (
        target.name
      ),
    })),
    ...(drvc?.configuration?.configuration_type ===
    DRVCTypesType.MEASUREMENT_SERIES
      ? [
          {
            title: "Date Range",
            value: dateRange,
          },
        ]
      : []),
    ...(window
      ? [
          {
            title: "Window",
            value: window,
          },
        ]
      : []),
    {
      title: "Default Value",
      description:
        "Default Value will be used as the record source when automation is applied to a property that 1) does not have an automation configured 2) has an automation configured but a record value has not yet been auto-generated",
      value: (
        <NumberDataDisplay
          value={drvc?.default_value ?? "-"}
          unit={
            drvc?.measurement_unit ? getUnitName(drvc.measurement_unit) : ""
          }
          showInvalidIndicator={false}
        />
      ),
    },
  ];

  return (
    <Drawer
      size="md"
      isOpen={isOpen}
      onClose={onClose}
      title={`${
        drvc?.measurement_type ? `${getTypeName(drvc.measurement_type)} ` : ""
      }Automation Details`}
    >
      <KeyValueList
        data={data}
        variant="shaded"
        isLoading={isLoading}
      />
    </Drawer>
  );
};
